import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import { API_URL } from "../config/api";
import { filterTypedWord, WORD_MOT_MAX_LENGTH } from "../utils/wordSanitize";
import "./DeleteWordModal.css";

function normalizeSpelling(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function countSignificantChars(value) {
  return normalizeSpelling(value).replace(/\s+/g, "").length;
}

export default function RenameWordSpellingModal({ word, open, onClose, onRenamed }) {
  const [newSpelling, setNewSpelling] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setNewSpelling("");
    setSubmitting(false);
    setError(null);
  }, [open, word]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !word) return null;

  const currentSpelling = normalizeSpelling(word);
  const nextSpelling = normalizeSpelling(newSpelling);
  const isIdentical = Boolean(nextSpelling) && nextSpelling === currentSpelling;
  const isTooShort = countSignificantChars(newSpelling) < 3;
  const canSubmit = Boolean(nextSpelling) && !isIdentical && !isTooShort && !submitting;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_URL}/mots/${encodeURIComponent(word)}/orthographe`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nouveauMot: newSpelling }),
        }
      );
      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.message || "Impossible de modifier l'orthographe.");
      }

      const renamedTo = result.mot?.mot || nextSpelling;
      const previous = result.ancienMot || currentSpelling;
      onRenamed?.(previous, renamedTo);
      onClose();
    } catch (err) {
      setError(err.message || "Erreur lors de la modification.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="delete-word-modal delete-word-modal--rename"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-word-modal-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3
          id="rename-word-modal-title"
          className="delete-word-modal__title delete-word-modal__title--gothic"
        >
          Modifier l'orthographe
        </h3>

        <div className="delete-word-modal__field">
          <span className="delete-word-modal__field-label">Orthographe actuelle</span>
          <p className="delete-word-modal__current">{word}</p>
        </div>

        <label className="delete-word-modal__field" htmlFor="rename-word-new-spelling">
          <span className="delete-word-modal__field-label">Nouvelle orthographe</span>
          <input
            id="rename-word-new-spelling"
            type="text"
            className="delete-word-modal__input"
            value={newSpelling}
            onChange={(event) => setNewSpelling(filterTypedWord(event.target.value))}
            maxLength={WORD_MOT_MAX_LENGTH}
            placeholder="Saisissez la forme corrigée…"
            autoFocus
            disabled={submitting}
            autoComplete="off"
            spellCheck="false"
            onKeyDown={(event) => {
              if (event.key === "Enter" && canSubmit) {
                event.preventDefault();
                handleConfirm();
              }
            }}
          />
        </label>

        <p className="delete-word-modal__hint">
          Les statistiques de ce mot seront effacées. L'historique des parties
          déjà jouées n'est pas modifié.
        </p>

        {isIdentical ? (
          <p className="delete-word-modal__error">
            La nouvelle orthographe est identique à l'actuelle.
          </p>
        ) : null}

        {error ? <p className="delete-word-modal__error">{error}</p> : null}

        <div className="delete-word-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--primary"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? "Enregistrement…" : "Valider"}
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
